import React, { useState, useRef } from 'react'
import {
  Box,
  Card,
  ClickAwayListener,
  Grid,
  Paper,
  Popper,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { connect } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import cn from 'classnames'
import MapPreview from '../common/MapPreview'
import {
  selectCurrentLocationWithAddress,
  selectCurrentSavedLocation,
} from '../../redux/modules/selectors'
import LinkButton from '../common/LinkButton'
import { themeConWeb } from './../../util/modernThemeConweb'
import { formatAddress } from './../../util/formatUtils'
import LocationSearchWithScripts from './LocationSearch'

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    padding: '0',
  },
  mapBox: {
    alignItems: 'center',
    display: 'flex',
    minHeight: '150px',
  },
  addressBox: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  addressLine: {
    fontSize: `${themeConWeb.fontSizes.baseMinus1}`,
    margin: '2px 0',
  },
  firstAddressLine: {
    fontSize: `${themeConWeb.fontSizes.base}`,
    fontWeight: 500,
  },
  changeAddressButton: {
    cursor: 'pointer',
    fontSize: `${themeConWeb.fontSizes.baseMinus2}`,
    margin: '6px 0',
  },
  [theme.breakpoints.up('sm')]: {
    addressBox: {
      alignItems: 'center',
      flexDirection: 'row',
      padding: '20px',
    },
    firstAddressLine: {
      fontSize: `${themeConWeb.fontSizes.basePlus1}`,
    },
    changeAddressButton: {
      fontSize: `${themeConWeb.fontSizes.baseMinus1}`,
      margin: '12px auto',
    },
  },
  [theme.breakpoints.up('md')]: {
    card: {
      flexDirection: 'column',
      minHeight: '332px',
      width: '240px',
    },
    addressBox: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
    changeAddressButton: {
      fontSize: `${themeConWeb.fontSizes.baseMinus2}`,
      margin: '12px 0',
    },
  },
}))

export const fillAddressLines = address => {
  let { name, deliveryLocationName, street, city, state, zip } = address
  return [name || deliveryLocationName, street, `${city}, ${state} ${zip}`].filter(Boolean)
}

const HomeLocationWidget = ({ location, currentSavedLocation }) => {
  const classes = useStyles()
  const cardRef = useRef()
  const addressRef = useRef()
  const [anchorElement, setAnchorElement] = useState(null)
  const desktopView = useMediaQuery(theme => theme.breakpoints.up('md'))
  const mobileView = useMediaQuery(theme => theme.breakpoints.down('xs'))

  return (
    <Card className={classes.card} ref={cardRef}>
      <Grid container>
        <Grid className={classes.mapBox} item xs={5} sm={4} md={12}>
          {location && (
            <MapPreview
              height={desktopView ? 241 : 150}
              address={formatAddress(location.address)}
            />
          )}
        </Grid>
        <Grid className={classes.addressBox} item xs={7} sm={8} md={12}>
          <Box ref={addressRef} data-testid="addressListContainer">
            {location &&
              fillAddressLines(currentSavedLocation || location).map((line, i) => {
                return (
                  <Typography
                    key={`${line}-${i}`}
                    className={cn(classes.addressLine, { [classes.firstAddressLine]: i === 0 })}
                  >
                    {line}
                  </Typography>
                )
              })}
          </Box>
          <LinkButton
            className={classes.changeAddressButton}
            color={themeConWeb.color.dangerLight}
            onClick={() => {
              setAnchorElement(desktopView ? addressRef.current : cardRef.current)
            }}
          >
            Change Location
          </LinkButton>
        </Grid>
      </Grid>
      <Popper
        anchorEl={anchorElement}
        id="location-switcher"
        modifiers={{
          flip: {
            enabled: false,
          },
          preventOverflow: {
            boundariesElement: 'scrollParent',
            enabled: true,
          },
        }}
        open={!!anchorElement}
        placement="bottom"
      >
        <ClickAwayListener onClickAway={() => setAnchorElement(null)}>
          <Box width={mobileView ? 300 : 320} data-testid="locationSearchContainer">
            <Paper>
              <LocationSearchWithScripts />
            </Paper>
          </Box>
        </ClickAwayListener>
      </Popper>
    </Card>
  )
}

const mapStateToProps = state => {
  const location = selectCurrentLocationWithAddress(state)
  const currentSavedLocation = selectCurrentSavedLocation(state)

  return {
    currentSavedLocation,
    location,
  }
}

export default connect(mapStateToProps)(HomeLocationWidget)
